import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import SearchBand from "@/pages/home/components/SearchBand";
import CategoryEntries from "@/pages/home/components/CategoryEntries";
import HotList from "@/pages/home/components/HotList";
import NewsTicker from "@/pages/home/components/NewsTicker";
import { allMovies, allNews, sortByKey } from "@/utils/catalog";

export default function Home() {
  const heroItems = allMovies.filter((movie) => movie.heroImage).slice(0, 5);
  const latestMovies = sortByKey(allMovies, "updated").slice(0, 8);
  const hotItems = allMovies.filter((movie) => movie.hot).slice(0, 6);

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar />

      <main className="mx-auto w-full max-w-[1320px] px-4 py-6 md:px-6 md:py-8">
        <HeroCarousel items={heroItems} />

        <div className="mt-6">
          <SearchBand />
        </div>

        <section className="mt-12">
          <SectionHeading title="分类快捷入口" subtitle="按你喜欢的类型，快速进入对应频道" />
          <CategoryEntries />
        </section>

        <section className="mt-12">
          <SectionHeading
            title="最新上映"
            subtitle="近期更新与上映的电影、剧集，第一时间掌握"
            action={
              <Link
                to="/category/movie"
                className="hidden items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 sm:flex"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <SectionHeading title="热门推荐" subtitle="编辑精选，值得一看的高分佳作" />
            <HotList items={hotItems} />
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-sparkling-2-line text-2xl" />
              </span>
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground-950">本周编辑手记</h3>
                <p className="text-xs text-foreground-500">为你解读当周最值得关注的内容</p>
              </div>
            </div>
            <div className="mt-5 space-y-4">
              {allNews.slice(0, 4).map((item) => (
                <Link
                  key={item.id}
                  to="/search"
                  className="group flex items-start gap-3 border-b border-background-200 pb-4 last:border-b-0 last:pb-0"
                >
                  <span className="mt-0.5 flex h-6 shrink-0 items-center rounded-md bg-accent-100 px-2 text-[11px] font-medium text-accent-700">
                    {item.category}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm text-foreground-800 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </p>
                    <span className="mt-1 block text-xs text-foreground-400">{item.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-12">
          <NewsTicker items={allNews} />
        </section>

        <section className="mt-12 overflow-hidden rounded-lg bg-primary-950">
          <div className="grid grid-cols-1 items-center gap-8 p-6 md:grid-cols-2 md:p-12">
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
                关于蘑菇视频
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-200/85 md:text-base">
                蘑菇视频致力于为影视爱好者打造一个清晰、便捷的高清影视内容平台。我们汇聚电影、电视剧与综艺等多元内容，
                提供多维筛选、编辑精选推荐与资讯快讯，让你更轻松地发现好片、追到好剧。
              </p>
              <div className="mt-6 flex flex-wrap gap-6">
                <div>
                  <p className="font-heading text-2xl font-extrabold text-primary-300">5000+</p>
                  <p className="mt-1 text-xs text-background-200/70">影视内容总量</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-extrabold text-primary-300">4</p>
                  <p className="mt-1 text-xs text-background-200/70">核心内容频道</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-extrabold text-primary-300">每日</p>
                  <p className="mt-1 text-xs text-background-200/70">资讯与更新</p>
                </div>
              </div>
              <Link
                to="/about"
                className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                了解更多
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="h-64 overflow-hidden rounded-lg md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20cozy%20home%20cinema%20viewing%20room%20with%20a%20large%20screen%20glowing%20warm%20amber%20light%2C%20comfortable%20sofa%20and%20popcorn%2C%20inviting%20movie%20night%20atmosphere%2C%20soft%20cinematic%20lighting%2C%20no%20text&width=800&height=640&seq=mogu-intro&orientation=landscape"
                alt="蘑菇视频观影场景"
                title="蘑菇视频 - 高清影视资源平台"
                className="h-full w-full object-top"
              />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}